import { Navigate, useParams } from "@/lib/router-compat";
import LocalLandingLayout from "@/components/local/LocalLandingLayout";
import { cityBySlug, cityPath, tradeBySlug, tradePath, trades } from "@/lib/localSeo";

const CityLanding = () => {
  const { city: citySlug } = useParams();

  // /contractor-websites/:city also serves the trade hubs (":trade-contractors").
  if (citySlug?.endsWith("-contractors")) {
    const trade = tradeBySlug(citySlug.replace(/-contractors$/, ""));
    if (!trade) return <Navigate to="/contractor-websites" replace />;
  }

  const city = cityBySlug(citySlug);
  if (!city) return <Navigate to="/king-county-contractors" replace />;

  const tradeLinks = trades.map((t) => ({
    label: `${t.label} web design in ${city.name}`,
    to: tradePath(t),
    note: t.buyer,
  }));

  const nearbyLinks = city.nearby
    .map((slug) => cityBySlug(slug))
    .filter(Boolean)
    .map((c) => ({ label: `Contractor web design in ${c!.name}`, to: cityPath(c!) }));

  return (
    <LocalLandingLayout
      title={`Contractor Web Design & SEO in ${city.name}, WA | jaik`}
      description={`Contractor web design and local SEO for ${city.name}, WA trades. Built to rank in ${city.name} search and turn visitors into booked jobs. Free mockup.`}
      canonicalPath={cityPath(city)}
      eyebrow={`${city.county} · ${city.name}, WA`}
      h1={`Contractor Web Design and Local SEO in ${city.name}, WA`}
      lead={`I design and maintain websites for ${city.name} contractors, then do the on-page and local SEO that gets those sites found. One person, no account managers, no long contracts.`}
      bodyHeading={`What the ${city.name} market actually looks like`}
      body={city.market}
      checklistTitle={`Included for ${city.name} trades`}
      checklist={[
        `A ${city.name} location page written around how people here actually search`,
        "Service pages for each job type you want more of",
        "Title tags, meta descriptions, headings and image alt text done properly",
        "Google Business Profile kept current, with review responses written for you",
        "Click-to-call, quote forms and booking links that work on a phone",
        "Hosting, backups, security and monthly updates handled",
      ]}
      areasTitle={`Neighborhoods and areas covered in ${city.name}`}
      areas={city.areas}
      linkSections={[
        {
          title: `${city.name} web design by trade`,
          intro: `Each trade sells differently, so each one gets its own page structure and its own keyword set.`,
          links: tradeLinks,
        },
        { title: `Nearby cities`, links: nearbyLinks },
      ]}
      faqs={[
        {
          q: `Do you only work with contractors in ${city.name}?`,
          a: `I work across the Puget Sound region, but the pages, photos and keywords are written for ${city.name} specifically. A generic Seattle page will not outrank a page built for ${city.name}.`,
        },
        {
          q: `How long until I rank for ${city.name} searches?`,
          a: `Smaller Puget Sound cities often move within four to eight weeks. Seattle and Bellevue terms take longer, usually three to six months of steady work.`,
        },
        {
          q: "What does it cost?",
          a: "Monthly plans start at $400 with a one-time setup fee. The Growth plan at $750 a month covers most contractors, and it includes a digital business card and QR code.",
        },
        {
          q: "Can I see something before I commit?",
          a: "Yes. Book a 20 minute call and I will build a free mockup of your homepage so you can judge the work before spending anything.",
        },
      ]}
      crumbs={[
        { name: "Home", path: "/" },
        { name: "King County Contractors", path: "/king-county-contractors" },
        { name: city.name, path: cityPath(city) },
      ]}
      serviceName={`Contractor web design and SEO in ${city.name}, WA`}
      areaServed={[city.name, ...city.nearby.map((s) => cityBySlug(s)?.name ?? "").filter(Boolean)]}
    />
  );
};

export default CityLanding;
